import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useGetMeQuery } from './auth/authApiSlice';
import { useWebcamCapture } from "./chat_sessions/useWebcamCapture";
import CameraFeed, { CAMERA_FEED_Z_INDEX } from "./chat_sessions/CameraFeed";
import { baseUrl, withWsToken } from '../app/api/apiSlice';
import { useFetchChatSessionByIdQuery } from './chat_sessions/chatSessionApiSlice';
import { useCreateEvaluationSessionMutation } from './evaluation_sessions/evaluationSessionApiSlice';
import { useGetEvaluationCriteriaGroupsQuery } from './evaluation_criteria_group/evaluationCriteriaGroupApiSlice';
import { canRequestEvaluation } from './evaluation_sessions/evaluationRules';
import AudioOrbVisual from './visuals/AudioOrbVisual';
import AICoachingPanel from './visuals/AICoachingPanel';
import { Header, Button, Dropdown, Icon, Message, Modal } from 'semantic-ui-react';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { NAV } from '../navLabels';
import { nextGain, framePeakOf } from './inputGain';
import { loopbackEnabled, playViaLoopback } from './aecLoopback';
import { GIT_SHA } from '../version';
import { uploadFileToS3Presigned, reportAvUploadFailure } from '../utils/presignedUpload';
const VIEWPORT_HEIGHT = typeof CSS !== 'undefined' && CSS.supports && CSS.supports('height', '100dvh')
  ? 'calc(100dvh - 50px)'
  : 'calc(100vh - 50px)';

/**
 * Turn state enum matching backend TurnState
 */
const TurnState = {
  IDLE: 'idle',
  USER_SPEAKING: 'user_speaking',
  AI_RESPONDING: 'ai_responding'
};

const SHOW_AI_COACHING = false; // ACE feedback CSV 24

// Matches OpenAIRealtimeConsumer.LIMIT_CLOSE_CODE. Not a dropped connection, so the
// automatic reconnect must not treat it as one.
const LIMIT_CLOSE_CODE = 4002;

// Staff-only realtime backend pick (#177). Keys are the ws/api/<key>-realtime/ route names.
const PROVIDER_LABEL = { openai: 'OpenAI', grok: 'Grok', gemini: 'Gemini', gptlive: 'OpenAI Live' };

const PROVIDER_OPTIONS = Object.entries(PROVIDER_LABEL).map(([value, text]) => ({ key: value, value, text }));

// Header control, under the persona name. Before connect: the site's standard selection
// dropdown (same as 파트너 선택 elsewhere), kept white on purpose. After connect it becomes
// the locked value as plain text, in the same slot so nothing shifts.
const ProviderPicker = ({ value, onChange, locked, isMobile }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '0.75rem', color: 'rgba(255,255,255,0.85)', fontSize: isMobile ? '1rem' : '0.95rem' }}>
    <span style={{ whiteSpace: 'nowrap' }}>AI 음성 엔진</span>
    {locked ? (
      <strong style={{ color: 'white' }}>{PROVIDER_LABEL[value]}</strong>
    ) : (
      <Dropdown
        selection
        options={PROVIDER_OPTIONS}
        value={value}
        onChange={(e, { value: v }) => onChange(v)}
        style={{ minWidth: isMobile ? '200px' : '180px', fontSize: isMobile ? '1.05rem' : undefined }}
      />
    )}
  </div>
);

const AudioOrbOpenAI = () => {
  const { isMobile } = useBreakpoint();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isConnected, setIsConnected] = useState(false);
  // Realtime backend: the contractor's realtime_provider (Django admin), locked for the life
  // of the session (a reconnect after a drop must land on the same consumer). When the
  // contractor is opted in (realtime_provider_selectable) the trainee may override it
  // before 세션 시작, remembered per browser. Read from a fresh /users/me/ rather than the
  // login-time copy so an admin flip needs no re-login. The server enforces the same
  // rule on the Grok, Gemini and GPT-Live routes, so this is display, not the gate.
  const { data: me } = useGetMeQuery(undefined, { refetchOnMountOrArgChange: true });
  const canPickProvider = me?.contractor?.realtime_provider_selectable === true;
  const contractorProvider = PROVIDER_LABEL[me?.contractor?.realtime_provider] ? me.contractor.realtime_provider : 'openai';
  const [provider, setProvider] = useState(() => {
    const stored = localStorage.getItem('realtimeProvider');
    return PROVIDER_LABEL[stored] ? stored : null;
  });
  const sessionProviderRef = useRef(null); // set at connect, cleared at manual end
  const activeProvider = canPickProvider ? (provider ?? contractorProvider) : contractorProvider;
  const pickProvider = (p) => {
    setProvider(p);
    try { localStorage.setItem('realtimeProvider', p); } catch (e) { /* private mode */ }
  };
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState(null);
  const [volume, setVolume] = useState(0);
  const sessionId = searchParams.get('session_id');
  
  // Turn state
  const [turnState, setTurnState] = useState(TurnState.IDLE);
  
  // Transcript state - separate current (accumulating) from display (stable)
  const [userTranscript, setUserTranscript] = useState("");      // Current user turn
  const [aiTranscript, setAiTranscript] = useState("");          // Current AI turn
  // Finished lines of this connection, mirroring the ChatLogs the server saves: one per trainee
  // transcript (a Grok `continues` replaces the last one) and one per customer response at
  // turn_complete. The WS handler is bound at connect time, so the customer's text goes
  // through a ref, not state.
  const [liveLogs, setLiveLogs] = useState([]);
  const aiLineRef = useRef("");
  const historyBoxRef = useRef(null);
  
  // Guide/coaching state
  const [monitoringEnabled, setMonitoringEnabled] = useState(false);
  const [guideMessage, setGuideMessage] = useState("");
  const [customerInsights, setCustomerInsights] = useState([]);
  const [currentPhase, setCurrentPhase] = useState("");
  const [recommendedProduct, setRecommendedProduct] = useState("");
  const [productSpecs, setProductSpecs] = useState([]);
  const [pinnedProduct, setPinnedProduct] = useState(null);
  const [newSuggestion, setNewSuggestion] = useState(null);
  
  const { data: chatSession } = useFetchChatSessionByIdQuery(sessionId, { skip: !sessionId, refetchOnMountOrArgChange: true });
  const currentPersona = chatSession?.persona;
  // Lines saved before this page opened — 이어하기 resumes a session that already has some.
  // Fetched fresh: a cached copy would miss the lines from the last visit.
  const pastLogs = [...(chatSession?.chatlog_set || [])]
    .filter(l => l.utterance && l.utterance.trim())
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    .map(l => ({ speaker: l.speaker, text: l.utterance }));
  const history = [...pastLogs, ...liveLogs];
  // 연습 sessions are never graded, so the evaluate button is not offered. The server
  // rejects evaluation of a practice session regardless (EvaluationSessionSerializer).
  const isPractice = !!chatSession?.is_practice;

  // Evaluation trigger. Ending the session used to do nothing — the trainee had to find the
  // session under 대화 내역 and run 평가 실행 from the text view, which most never did.
  const [userTurns, setUserTurns] = useState(0);
  // Speak-first hint. The AI customer waits for the trainee to open, so a trainee who waits
  // for the customer gets silence — and a mic that captures nothing looks the same. Support
  // ticket 2026-10-19: three LGU sessions in KakaoTalk's in-app browser, no line on either side.
  const [hintStuck, setHintStuck] = useState(false);
  // Session time limit. The server is authoritative: it sends how long this session may
  // run (for practice that is the remaining cumulative budget, not a fixed cap) and closes
  // the socket at the deadline. This countdown is display only.
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [limitWarning, setLimitWarning] = useState(false);
  const [limitReached, setLimitReached] = useState(null);
  const [evalError, setEvalError] = useState(null);
  // Audio or webcam upload to S3 failed; the trainee is told so the session isn't
  // silently unrecorded.
  const [avUploadError, setAvUploadError] = useState(false);
  const { data: criteriaGroups } = useGetEvaluationCriteriaGroupsQuery();
  const [createEvaluationSession, { isLoading: isCreatingEval }] =
    useCreateEvaluationSessionMutation();
  const canEvaluate = canRequestEvaluation(pastLogs.filter(l => l.speaker === 'H').length + userTurns);

  // Tick locally so the number moves; the server still owns the actual cutoff.
  // Only while connected: this is the remaining CUMULATIVE budget, so ticking it down
  // after the trainee presses 종료 both misreports the budget and makes Stop look like
  // it did nothing. Reconnecting resumes the tick.
  useEffect(() => {
    if (!isConnected) return undefined;
    if (secondsLeft === null || secondsLeft <= 0) return undefined;
    const id = setInterval(
      () => setSecondsLeft(s => (s === null ? null : Math.max(0, s - 1))),
      1000,
    );
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, secondsLeft === null, secondsLeft === 0]);

  const formatRemaining = (total) => {
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const {
    videoRef,
    consentGiven,
    isCapturing,
    cameraError,
    uploadFailed: webcamUploadFailed,
    handleConsent,
    startCapture,
    stopCapture,
  } = useWebcamCapture({ sessionId });

  // Refs
  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const sourceRef = useRef(null);
  const outputNodeRef = useRef(null);
  const loopbackCleanupRef = useRef(null); // set when the customer's voice plays via aecLoopback
  const analyserRef = useRef(null);
  // Adaptive mic gain state; see inputGain.js for why this is needed.
  const agcRef = useRef({ envelope: 0, gain: 1 });
  const animationFrameRef = useRef(null);
  const streamRef = useRef(null);
  // Ring buffer for gapless AI audio playback (pull model)
  const outputProcessorRef = useRef(null);
  const ringBufferRef = useRef(null);
  const ringWritePosRef = useRef(0);
  const ringReadPosRef = useRef(0);
  const playbackStartedRef = useRef(false);
  // Recording: captures mic + AI audio mixed in real-time
  const mediaRecorderRef = useRef(null);
  const recordingChunksRef = useRef([]);

  // Transcript delta batching: many deltas arrive per second during a turn;
  // coalescing them to one setState per frame keeps the main thread free
  // for the audio scheduler so it doesn't underrun mid-response.
  const pendingUserDeltaRef = useRef("");
  const pendingAiDeltaRef = useRef("");
  const transcriptFlushRafRef = useRef(null);

  // Diagnostic counters for ring-buffer playback health (per AI response).
  const audioStatsRef = useRef({
    chunksArrived: 0,
    samplesWritten: 0,
    samplesPlayed: 0,
    underrunFrames: 0,
    maxBacklog: 0,
    lapDetected: false,
    startedAt: 0,
  });

  // Reconnection refs
  const isManualDisconnect = useRef(false);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 5;

  useEffect(() => {
    return () => {
      isManualDisconnect.current = true;
      disconnect();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (transcriptFlushRafRef.current != null) {
        cancelAnimationFrame(transcriptFlushRafRef.current);
        transcriptFlushRafRef.current = null;
      }
    };
  }, []);

  // One socket per page. 세션 시작 stayed pressable while the socket was opening (a few
  // seconds on mobile), and each press opened another socket plus another mic pipeline, all
  // sending to the newest socket: the AI heard one endless garbled utterance and never
  // replied (support ticket 750ac176, 2026-10-20: 3 sockets in 14 s).
  const [connecting, setConnecting] = useState(false);
  const connect = () => {
    if (wsRef.current && wsRef.current.readyState <= WebSocket.OPEN) return;
    if (!sessionId) {
        setError("No chat session ID provided.");
        return;
    }
    setError(null);
    isManualDisconnect.current = false;
    
    // First connect locks the provider; the reconnect path reuses it.
    if (!sessionProviderRef.current) sessionProviderRef.current = activeProvider;
    const provider = sessionProviderRef.current;
    const wsProtocol = baseUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = baseUrl.replace(/^https?:\/\//, '');
    const wsUrl = withWsToken(`${wsProtocol}://${wsHost}/ws/api/${provider}-realtime/${sessionId}/`);

    setConnecting(true);
    const ws = new WebSocket(wsUrl);
    ws.binaryType = 'arraybuffer';

    ws.onopen = () => {
      console.log(`Connected to ${provider} realtime session`);
      setError(null);
      setIsConnected(true);
      setConnecting(false);
      reconnectAttemptsRef.current = 0; // Reset attempts on success
      startRecording();
      startCapture();

      // Send initial monitoring state
      ws.send(JSON.stringify({
        type: "toggle_monitoring",
        enabled: monitoringEnabled
      }));
    };

    ws.onmessage = async (event) => {
      if (event.data instanceof ArrayBuffer) {
        handleAudioData(event.data);
      } else if (typeof event.data === 'string') {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (e) {
          console.error("Error parsing JSON message", e);
        }
      }
    };

    ws.onclose = (event) => {
      console.log('Disconnected from OpenAI Realtime Session', event.code);
      setIsConnected(false);
      setConnecting(false);
      stopRecording();

      if (isManualDisconnect.current) {
         // Expected disconnect
         return;
      }

      if (event.code === 4000) {
          setError("Missing API_KEY on backend.");
      } else if (event.code === LIMIT_CLOSE_CODE) {
          // Time limit reached, or the practice budget is spent. Terminal: reconnecting
          // would fight a server-side rule, and the session_limit_reached frame has
          // already told the trainee what happened.
          setError(null);
      } else if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          // Attempt reconnect
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 10000);
          console.log(`Connection dropped (Code: ${event.code}). Reconnecting in ${delay}ms... (Attempt ${reconnectAttemptsRef.current + 1}/${MAX_RECONNECT_ATTEMPTS})`);
          
          setError(`Connection dropped. Reconnecting... (${reconnectAttemptsRef.current + 1})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
              reconnectAttemptsRef.current += 1;
              connect();
          }, delay);
      } else {
          setError("Connection failed. Please refresh or try again later.");
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    wsRef.current = ws;
  };

  const disconnect = () => {
    isManualDisconnect.current = true;
    sessionProviderRef.current = null; // next 세션 시작 may pick again
    stopCapture();
    if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    stopRecording();
  };

  // Asked first: one graded evaluation per session (client copy, 2026-10-20).
  const [evalConfirmOpen, setEvalConfirmOpen] = useState(false);
  const endSessionAndEvaluate = async () => {
    setEvalConfirmOpen(false);
    setEvalError(null);
    const criteriaGroupId = criteriaGroups?.results?.[0]?.id;
    if (!criteriaGroupId) {
      setEvalError("평가 기준을 불러오지 못했습니다. 대화 내역에서 다시 시도해 주세요.");
      return;
    }
    disconnect();
    const result = await createEvaluationSession({
      chat_session: sessionId,
      criteria_group: criteriaGroupId,
    });
    if (result.error) {
      // The session itself is saved, so the 대화 내역 route still works as a fallback.
      setEvalError("평가를 시작하지 못했습니다. 대화 내역에서 다시 시도해 주세요.");
      return;
    }
    navigate(`/chatsessions/${sessionId}`);
  };

  // Same guard for the mic: a second pipeline would interleave its audio into the socket.
  const recordingStartingRef = useRef(false);
  const startRecording = async () => {
    if (streamRef.current || recordingStartingRef.current) return;
    recordingStartingRef.current = true;
    try {
      await startRecordingOnce();
    } finally {
      recordingStartingRef.current = false;
    }
  };
  const startRecordingOnce = async () => {
    try {
      if (!navigator.mediaDevices) {
        throw new Error("Microphone access requires a secure context (HTTPS) or localhost. If you are on mobile, please use HTTPS.");
      }

      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          autoGainControl: true,
          noiseSuppression: true,
        }
      });
      
      streamRef.current = stream;
      agcRef.current = { envelope: 0, gain: 1 };
      
      // Create AudioContext if it doesn't exist
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
      }

      // Create output node (GainNode) for visualizer + recording
      if (!outputNodeRef.current) {
          outputNodeRef.current = audioContextRef.current.createGain();
          // Through a WebRTC loopback where that lets the echo canceller see it (aecLoopback.js);
          // straight to the speakers otherwise, or if the loopback can't start.
          let routed = false;
          let route = 'direct';
          if (loopbackEnabled()) {
            try {
              loopbackCleanupRef.current = await playViaLoopback(audioContextRef.current, outputNodeRef.current);
              routed = true;
              route = 'loopback';
              console.log('[audio] customer voice via WebRTC loopback (AEC reference)');
            } catch (e) {
              route = 'fallback';
              console.warn('[audio] loopback playout failed, using direct output:', e);
            }
          }
          if (!routed) outputNodeRef.current.connect(audioContextRef.current.destination);
          // Saved on the session (ChatSession.client_audio) so an "it heard itself" report
          // can be checked against what actually ran.
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
              type: 'client_info', audio_route: route, version: GIT_SHA, ua: navigator.userAgent,
            }));
          }
      }

      // Ring buffer: ScriptProcessor pulls audio data continuously → no gaps between chunks.
      // Sized generously (20s) so that frame-buffering proxies (e.g. AWS ALB) can deliver
      // multi-second bursts of audio.delta without the writer lapping the reader, which
      // would manifest as "out-of-order" sounding playback.
      const RING_SIZE = 480000; // 20 seconds at 24kHz
      const PREBUFFER_SAMPLES = 12000; // 500ms pre-buffer before starting playback
      ringBufferRef.current = new Float32Array(RING_SIZE);
      ringWritePosRef.current = 0;
      ringReadPosRef.current = 0;
      playbackStartedRef.current = false;
      outputProcessorRef.current = audioContextRef.current.createScriptProcessor(2048, 0, 1);
      outputProcessorRef.current.onaudioprocess = (e) => {
        const output = e.outputBuffer.getChannelData(0);
        const rb = ringBufferRef.current;
        if (!rb) { output.fill(0); return; }
        const writePos = ringWritePosRef.current;
        let readPos = ringReadPosRef.current;
        const available = writePos - readPos;
        if (!playbackStartedRef.current) {
          if (available < PREBUFFER_SAMPLES) { output.fill(0); return; }
          playbackStartedRef.current = true;
        }
        let nonZero = 0;
        for (let i = 0; i < output.length; i++) {
          if (readPos < writePos) {
            output[i] = rb[readPos++ % RING_SIZE];
            nonZero++;
          } else {
            output[i] = 0;
          }
        }
        ringReadPosRef.current = readPos;
        const stats = audioStatsRef.current;
        stats.samplesPlayed += nonZero;
        if (playbackStartedRef.current && nonZero < output.length) {
          stats.underrunFrames++;
        }
      };
      outputProcessorRef.current.connect(outputNodeRef.current);
      
      sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
      
      // Analyser for visualizer
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      sourceRef.current.connect(analyserRef.current);

      // Use ScriptProcessor for audio processing
      // OpenAI expects PCM16 at 24000Hz, but we'll send at 16000Hz (it supports both)
      processorRef.current = audioContextRef.current.createScriptProcessor(4096, 1, 1);
      
      const inputSampleRate = audioContextRef.current.sampleRate;
      const targetSampleRate = 24000; // OpenAI prefers 24kHz
      
      processorRef.current.onaudioprocess = (e) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

        const inputData = e.inputBuffer.getChannelData(0);
        
        // Silence output to prevent feedback loop
        const outputData = e.outputBuffer.getChannelData(0);
        for (let i = 0; i < outputData.length; i++) {
            outputData[i] = 0;
        }

        // Quiet mics are boosted toward a usable level before they leave the browser:
        // xAI's VAD hears nothing below roughly -30 dBFS. Boost-only, so a healthy mic
        // is untouched. See inputGain.js.
        const agc = nextGain(framePeakOf(inputData), agcRef.current.envelope, agcRef.current.gain);
        agcRef.current = agc;
        const gain = agc.gain;

        // Resample to target sample rate
        let pcmData;
        
        if (inputSampleRate === targetSampleRate) {
            pcmData = new Int16Array(inputData.length);
            for (let i = 0; i < inputData.length; i++) {
                const s = Math.max(-1, Math.min(1, inputData[i] * gain));
                pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
            }
        } else {
            // Resample
            const ratio = inputSampleRate / targetSampleRate;
            const newLength = Math.floor(inputData.length / ratio);
            pcmData = new Int16Array(newLength);
            
            for (let i = 0; i < newLength; i++) {
                const offset = Math.floor(i * ratio);
                const s = Math.max(-1, Math.min(1, inputData[offset] * gain));
                pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
            }
        }
        
        wsRef.current.send(pcmData.buffer);
      };

      analyserRef.current.connect(processorRef.current);
      processorRef.current.connect(audioContextRef.current.destination);

      // MediaRecorder: mix mic + AI audio into one stream for recording
      const recordingDest = audioContextRef.current.createMediaStreamDestination();
      sourceRef.current.connect(recordingDest);
      outputNodeRef.current.connect(recordingDest);
      recordingChunksRef.current = [];
      const preferredMime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : '';
      const recorder = new MediaRecorder(recordingDest.stream, preferredMime ? { mimeType: preferredMime } : {});
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordingChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(recordingChunksRef.current, { type: preferredMime || recorder.mimeType });
        uploadAudioRecording(blob, preferredMime || recorder.mimeType);
      };
      recorder.start(1000);
      mediaRecorderRef.current = recorder;
      console.log('[audio] MediaRecorder started, mimeType:', recorder.mimeType);

      setIsRecording(true);
      // Start volume update loop
      requestAnimationFrame(updateVolume);

    } catch (err) {
      console.error('Error accessing microphone:', err);
      setError("Error accessing microphone: " + err.message);
    }
  };

  const updateVolume = () => {
      if (!analyserRef.current || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
      
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteFrequencyData(dataArray);
      
      // Calculate average volume
      let sum = 0;
      for(let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i];
      }
      const avg = sum / dataArray.length;
      
      // Scale down for visualizer
      setVolume(avg);
      
      animationFrameRef.current = requestAnimationFrame(updateVolume);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop(); // triggers onstop → uploadAudioRecording
      mediaRecorderRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect();
    }
    if (processorRef.current) {
      processorRef.current.disconnect();
    }
    if (outputProcessorRef.current) {
      outputProcessorRef.current.disconnect();
      outputProcessorRef.current = null;
    }
    ringBufferRef.current = null;
    ringWritePosRef.current = 0;
    ringReadPosRef.current = 0;
    playbackStartedRef.current = false;
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (loopbackCleanupRef.current) {
      loopbackCleanupRef.current();
      loopbackCleanupRef.current = null;
    }
    outputNodeRef.current = null;
    if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
    }
    setIsRecording(false);
    setVolume(0);
  };

  const uploadAudioRecording = async (blob, mimeType) => {
    const token = localStorage.getItem("token");
    const fail = (detail) => {
      console.error('[audio] Upload failed:', detail);
      setAvUploadError(true);
      reportAvUploadFailure(sessionId, token, 'audio', detail);
    };
    try {
      const contentType = mimeType.split(';')[0]; // "audio/webm;codecs=opus" → "audio/webm"
      const ext = contentType.includes('webm') ? '.webm' : contentType.includes('ogg') ? '.ogg' : '.mp4';
      const file = new File([blob], `session-audio${ext}`, { type: contentType });

      const { key, success, error } = await uploadFileToS3Presigned(file, token, `av/${sessionId}/audio/`);
      if (!success) return fail(error);

      // Save S3 key to session
      const saveRes = await fetch(`${baseUrl}/api/v1/resources/chat_sessions/${sessionId}/audio_recording/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ s3_key: key }),
      });
      if (!saveRes.ok) return fail(`Saving audio key failed: ${saveRes.status}`);

      console.log('[audio] Recording uploaded:', key);
    } catch (err) {
      fail(err.message || err);
    }
  };

  const clearAudioQueue = () => {
    ringReadPosRef.current = ringWritePosRef.current;
    playbackStartedRef.current = false;
  };

  const flushTranscriptDeltas = () => {
    transcriptFlushRafRef.current = null;
    if (pendingUserDeltaRef.current) {
      const d = pendingUserDeltaRef.current;
      pendingUserDeltaRef.current = "";
      setUserTranscript(prev => prev + d);
    }
    if (pendingAiDeltaRef.current) {
      const d = pendingAiDeltaRef.current;
      pendingAiDeltaRef.current = "";
      setAiTranscript(prev => prev + d);
    }
  };

  const scheduleTranscriptFlush = () => {
    if (transcriptFlushRafRef.current != null) return;
    transcriptFlushRafRef.current = requestAnimationFrame(flushTranscriptDeltas);
  };

  /**
   * Handle WebSocket messages from backend
   * Clean event-driven approach matching backend TurnState machine
   */
  // Shown until the session has a chat line — saved before (이어하기), the trainee's first
  // finished transcript, or the customer's first finished reply — not when speech merely
  // starts: a half-heard "안녕…" that never becomes a line shouldn't take the hint away.
  const awaitingFirstWords = isConnected && history.length === 0;
  // Kept mounted for the fade-out after it stops being needed.
  const [hintMounted, setHintMounted] = useState(false);

  useEffect(() => {
    if (awaitingFirstWords) {
      setHintMounted(true);
      // Still nothing after 12 s of a live session: say what to check, not just "speak".
      const t = setTimeout(() => setHintStuck(true), 12000);
      return () => clearTimeout(t);
    }
    setHintStuck(false);
    const t = setTimeout(() => setHintMounted(false), 800);
    return () => clearTimeout(t);
  }, [awaitingFirstWords]);

  // Keep the newest line in view.
  useEffect(() => {
    const box = historyBoxRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [history.length, userTranscript, aiTranscript]);

  // iOS suspends a page's audio on any interruption — a notification sound, a call, Siri,
  // switching apps, locking the screen — and never resumes it on its own, so the mic goes
  // silent with no error and the roleplay just "stops" (support tickets 195aba0b/09ab1a5b,
  // 2026-10-20: two graded sessions frozen ~1 min in, the socket still open). Watch the
  // AudioContext and the mic track while connected; resume when possible, otherwise ask
  // for a tap (iOS may only allow resume() from a user gesture).
  const [audioStalled, setAudioStalled] = useState(false);
  const audioHealthy = () => {
    const ctx = audioContextRef.current;
    const track = streamRef.current?.getAudioTracks?.()[0];
    return !!ctx && ctx.state === 'running' && !!track && track.readyState === 'live' && !track.muted;
  };
  useEffect(() => {
    if (!isConnected) { setAudioStalled(false); return; }
    let misses = 0;
    const check = () => {
      if (audioHealthy()) { misses = 0; setAudioStalled(false); return; }
      const ctx = audioContextRef.current;
      if (ctx && ctx.state !== 'running' && ctx.state !== 'closed') ctx.resume().catch(() => {});
      // Two misses in a row (~4 s): startRecording may still be setting up on the first.
      misses += 1;
      if (misses >= 2) setAudioStalled(true);
    };
    const t = setInterval(check, 2000);
    document.addEventListener('visibilitychange', check);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', check); };
  }, [isConnected]);
  const restoreAudio = async () => {
    const ctx = audioContextRef.current;
    const track = streamRef.current?.getAudioTracks?.()[0];
    if (ctx && track && track.readyState === 'live' && !track.muted) {
      try { await ctx.resume(); } catch (e) { /* fall through to a fresh mic */ }
      if (audioHealthy()) { setAudioStalled(false); return; }
    }
    // The mic itself is gone: take it again, as the reconnect path does (onclose → stop,
    // onopen → start). The socket and the conversation stay as they are.
    stopRecording();
    await startRecording();
    if (audioHealthy()) setAudioStalled(false);
  };

  const handleWebSocketMessage = (data) => {
    const { type } = data;
    
    // ============================================================
    // USER TURN EVENTS
    // ============================================================
    if (type === 'user_speaking_started') {
      pendingUserDeltaRef.current = "";
      setTurnState(TurnState.USER_SPEAKING);
      setUserTranscript("");
    }
    else if (type === 'user_speaking_stopped') {
      // User stopped speaking - waiting for transcript
    }
    else if (type === 'user_transcript_delta') {
      pendingUserDeltaRef.current += data.text;
      scheduleTranscriptFlush();
    }
    else if (type === 'user_transcript_done') {
      pendingUserDeltaRef.current = "";
      setUserTranscript("");
      setLiveLogs(logs => (data.continues && logs.length && logs[logs.length - 1].speaker === 'H')
        ? [...logs.slice(0, -1), { speaker: 'H', text: data.text }]
        : [...logs, { speaker: 'H', text: data.text }]);
      // One completed trainee transcript == one HUMAN ChatLog server-side (consumers_openai).
      // `continues`: Grok re-sent a paused line and the server updated that same ChatLog.
      if (!data.continues) setUserTurns(n => n + 1);
    }
    else if (type === 'user_transcript_failed') {
      console.error("User transcript failed:", data.error);
    }

    // ============================================================
    // SESSION TIME LIMIT
    // ============================================================
    else if (type === 'session_limits') {
      setSecondsLeft(data.seconds_allowed);
    }
    else if (type === 'session_limit_warning') {
      setSecondsLeft(data.seconds_remaining);
      setLimitWarning(true);
    }
    else if (type === 'session_limit_reached') {
      // The server closes the socket right after this. `exhausted` means the practice
      // budget or graded quota was already spent, so the session never started.
      setSecondsLeft(0);
      setLimitReached({
        isPractice: !!data.is_practice,
        exhausted: !!data.exhausted,
        resetsAt: data.resets_at || null,
      });
    }
    
    // ============================================================
    // AI TURN EVENTS
    // ============================================================
    else if (type === 'ai_responding_started') {
      pendingAiDeltaRef.current = "";
      aiLineRef.current = "";
      ringReadPosRef.current = ringWritePosRef.current;
      playbackStartedRef.current = false;
      audioStatsRef.current = {
        chunksArrived: 0,
        samplesWritten: 0,
        samplesPlayed: 0,
        underrunFrames: 0,
        maxBacklog: 0,
        lapDetected: false,
        startedAt: performance.now(),
      };
      setTurnState(TurnState.AI_RESPONDING);
      setAiTranscript("");
    }
    else if (type === 'ai_transcript_delta') {
      pendingAiDeltaRef.current += data.text;
      aiLineRef.current += data.text;
      scheduleTranscriptFlush();
    }
    else if (type === 'ai_transcript_done') {
      pendingAiDeltaRef.current = "";
      setAiTranscript(data.text);
      aiLineRef.current = data.text;
    }
    else if (type === 'ai_audio_interrupted') {
      console.log("AI audio interrupted");
      clearAudioQueue();
    }
    
    // ============================================================
    // TURN COMPLETION EVENTS
    // ============================================================
    else if (type === 'turn_complete') {
      const s = audioStatsRef.current;
      if (s.startedAt) {
        const elapsedMs = Math.round(performance.now() - s.startedAt);
        // Expected samples at 24kHz = elapsedMs * 24
        const expectedSamples = Math.round(elapsedMs * 24);
        console.log(
          `[audio-stats] turn done (status=${data.status}): ` +
          `elapsed=${elapsedMs}ms chunks=${s.chunksArrived} ` +
          `written=${s.samplesWritten} played=${s.samplesPlayed} ` +
          `expected=${expectedSamples} ` +
          `maxBacklog=${s.maxBacklog} underrunFrames=${s.underrunFrames} ` +
          `lap=${s.lapDetected}`
        );
      }
      setTurnState(TurnState.IDLE);
      const aiLine = aiLineRef.current;
      aiLineRef.current = "";
      setAiTranscript("");
      if (aiLine) {
        // Same suffix the server saves (consumers_openai, response.done).
        const suffix = data.status === 'cancelled' ? ' [Interrupted]' : '';
        setLiveLogs(logs => [...logs, { speaker: 'B', text: aiLine + suffix }]);
      }
    }
    
    // ============================================================
    // GUIDE/COACHING EVENTS
    // ============================================================
    else if (type === 'guide_message') {
      if (SHOW_AI_COACHING) {
        setGuideMessage(data.text);
      }
    }
    else if (type === 'guide_state') {
      if (SHOW_AI_COACHING) {
        const state = data.data;
        setCustomerInsights(state.customer_insights || []);
        setCurrentPhase(state.current_phase || "");
        
        const newProductName = state.recommended_product_name || "";
        const newProductSpecs = state.relevant_product_specs || [];
        
        if (newProductName) {
          setRecommendedProduct(newProductName);
          setProductSpecs(newProductSpecs);
          
          if (pinnedProduct && pinnedProduct.name !== newProductName) {
            setNewSuggestion({ name: newProductName, specs: newProductSpecs });
          } else {
            setNewSuggestion(null);
          }
        } else {
          if (!pinnedProduct) {
            setRecommendedProduct("");
            setProductSpecs([]);
          }
          setNewSuggestion(null);
        }
      }
    }
    
    // ============================================================
    // ERROR EVENTS
    // ============================================================
    else if (type === 'error') {
      setError(data.message || "An error occurred");
    }
    else if (type === 'monitoring_status') {
      console.log("Monitoring status:", data.enabled);
    }
  };

  const toggleMonitoring = () => {
    const newState = !monitoringEnabled;
    setMonitoringEnabled(newState);
    
    // Clear guide message when disabling
    if (!newState) {
        setGuideMessage("");
        setCustomerInsights([]);
        setCurrentPhase("");
        setRecommendedProduct("");
        setProductSpecs([]);
        setPinnedProduct(null);
        setNewSuggestion(null);
    }

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
            type: "toggle_monitoring",
            enabled: newState
        }));
    }
  };

  const handlePinProduct = () => {
    if (pinnedProduct) {
      // Unpin
      setPinnedProduct(null);
      setNewSuggestion(null);
    } else if (recommendedProduct) {
      // Pin current product
      setPinnedProduct({ name: recommendedProduct, specs: productSpecs });
      setNewSuggestion(null);
    }
  };

  const handleUpdatePinToNew = () => {
    if (newSuggestion) {
      setPinnedProduct({ name: newSuggestion.name, specs: newSuggestion.specs });
      setRecommendedProduct(newSuggestion.name);
      setProductSpecs(newSuggestion.specs);
      setNewSuggestion(null);
    }
  };

  // Determine what product to display
  const displayProduct = pinnedProduct || (recommendedProduct ? { name: recommendedProduct, specs: productSpecs } : null);

  const handleAudioData = (arrayBuffer) => {
    const rb = ringBufferRef.current;
    if (!rb) return;
    const int16Data = new Int16Array(arrayBuffer);
    let writePos = ringWritePosRef.current;
    const RING_SIZE = rb.length;
    for (let i = 0; i < int16Data.length; i++) {
      rb[writePos++ % RING_SIZE] = int16Data[i] / 32768.0;
    }
    ringWritePosRef.current = writePos;
    const stats = audioStatsRef.current;
    stats.chunksArrived++;
    stats.samplesWritten += int16Data.length;
    const backlog = writePos - ringReadPosRef.current;
    if (backlog > stats.maxBacklog) stats.maxBacklog = backlog;
    if (backlog > RING_SIZE) stats.lapDetected = true;
  };

  return (
    // dvh, not vh: on phones 100vh is the height with the browser toolbars hidden, so while they
    // show, the bottom controls (세션 시작) sat underneath them — and the chat history panel
    // took the swipe that used to scroll them into view. vh only where dvh isn't supported.
    <div style={{ position: 'relative', width: '100vw', height: VIEWPORT_HEIGHT, overflow: 'hidden', backgroundColor: '#0f172a' }}>
      
      {/* 동의 모달 — consentGiven이 false인 동안 전체 화면 오버레이 */}
      {!consentGiven && (
        <div style={{
          position: 'fixed', inset: 0,
          // Over the camera tile, which this dim overlay is meant to cover.
          zIndex: CAMERA_FEED_Z_INDEX + 10,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        }}>
          <div style={{
            background: 'rgba(30, 41, 59, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            backdropFilter: 'blur(12px)',
            padding: '28px 24px',
            width: '300px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>📷</div>
            <div style={{ color: 'white', fontWeight: '700', fontSize: '16px', marginBottom: '12px' }}>
              세션 녹화 안내
            </div>
            <div style={{ color: '#94a3b8', fontSize: '13px', lineHeight: '1.6', marginBottom: '20px' }}>
              <span style={{ color: '#e2e8f0' }}>웹캠 스냅샷(1분 간격)</span>과{' '}
              <span style={{ color: '#e2e8f0' }}>오디오</span>는 신원 확인 목적으로만 수집되며,
              관리자 외에는 접근할 수 없습니다.
            </div>
            <a
              href="/privacy#items"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                marginBottom: '18px',
                color: '#93c5fd',
                fontSize: '12px',
                textDecoration: 'underline',
              }}
            >
              개인정보처리방침 보기
            </a>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => navigate(-1)}
                style={{
                  flex: 1, padding: '9px', border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '8px', background: 'transparent',
                  color: '#94a3b8', cursor: 'pointer', fontSize: '13px',
                }}
              >
                거절
              </button>
              <button
                onClick={handleConsent}
                style={{
                  flex: 2, padding: '9px', border: 'none',
                  borderRadius: '8px', background: '#3b82f6',
                  color: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                }}
              >
                동의하고 시작
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 카메라 권한 거부 에러 배너 */}
      {cameraError && (
        <div style={{
          position: 'fixed', top: '60px', left: '50%', transform: 'translateX(-50%)',
          // Over the camera tile too — this banner is the message explaining why there
          // is nothing in it.
          zIndex: CAMERA_FEED_Z_INDEX + 20, background: 'rgba(239, 68, 68, 0.9)', borderRadius: '8px',
          padding: '10px 16px', color: 'white', fontSize: '13px',
        }}>
          {cameraError}
        </div>
      )}

      {(avUploadError || webcamUploadFailed) && (
        <div style={{
          position: 'fixed', top: cameraError ? '104px' : '60px', left: '50%', transform: 'translateX(-50%)',
          zIndex: CAMERA_FEED_Z_INDEX + 20, background: 'rgba(239, 68, 68, 0.9)', borderRadius: '8px',
          padding: '10px 16px', color: 'white', fontSize: '13px',
        }}>
          녹화 업로드에 실패했습니다. 관리자에게 알려주세요.
        </div>
      )}

      {/* Background Layer - Visualizer */}
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}>
        <AudioOrbVisual inputNode={sourceRef.current} outputNode={outputNodeRef.current} />
      </div>

      {/* Content Layer - UI Elements */}
      <div style={{ position: 'relative', zIndex: 10, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', pointerEvents: 'none', overflowY: 'auto' }}>
        
        {/* Top Section: Header */}
        <div style={{ padding: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'auto', background: 'linear-gradient(to bottom, rgba(0,0,0,0.8), transparent)' }}>
            <Header as='h2' style={{ color: 'white', marginBottom: '0.5rem' }}>
              {currentPersona ? `${currentPersona.name} (${currentPersona.gender==="MALE" ? "남" : "여"}, ${currentPersona.persona.age.value})` : NAV.ROLEPLAY}
            </Header>
            {canPickProvider && (
              <ProviderPicker
                value={isConnected && sessionProviderRef.current ? sessionProviderRef.current : activeProvider}
                onChange={pickProvider}
                locked={isConnected}
                isMobile={isMobile}
              />
            )}

            {/* Remaining time. Mandatory per client requests 7 and 10: for practice this is
                the remaining CUMULATIVE budget, not time elapsed in this session. Turns
                amber inside the warning window. */}
            {secondsLeft !== null && (
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '5px 12px',
                    marginBottom: '0.5rem',
                    borderRadius: '999px',
                    backgroundColor: limitWarning ? 'rgba(255,149,0,0.22)' : 'rgba(255,255,255,0.16)',
                    color: limitWarning ? '#FFCC80' : 'rgba(255,255,255,0.92)',
                    fontSize: '0.85rem',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  <Icon name="clock outline" style={{ margin: 0, fontSize: '0.85rem', lineHeight: 1 }} />
                  남은 시간 {formatRemaining(secondsLeft)}
                </div>
            )}

            {limitReached && (
                <Message
                  warning
                  style={{ marginBottom: '1rem', maxWidth: '520px' }}
                >
                  <Message.Header>
                    {!limitReached.exhausted
                      ? '시간이 종료되었습니다'
                      : limitReached.isPractice
                        ? '사전 연습 시간을 모두 사용했습니다'
                        : '롤플레이 횟수를 모두 사용했습니다'}
                  </Message.Header>
                  <p>
                    {limitReached.exhausted
                      ? limitReached.isPractice
                        ? '이번 주기의 사전 연습 시간을 모두 사용하셨습니다.'
                        : '이번 분기의 롤플레이 평가 횟수를 모두 사용하셨습니다.'
                      : limitReached.isPractice
                        ? '사전 연습 시간이 종료되어 세션이 종료되었습니다.'
                        : '롤플레이 제한 시간에 도달해 세션이 종료되었습니다.'}
                    {limitReached.resetsAt &&
                      ` ${new Date(limitReached.resetsAt).toLocaleDateString('ko-KR')}에 초기화됩니다.`}
                  </p>
                </Message>
            )}

            {/* Without this the missing 평가 받기 button just looks broken. */}
            {isPractice && (
                <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                  연습 모드 · 평가되지 않습니다
                </div>
            )}

            
            {error && (
                <Message negative style={{ marginBottom: '1rem' }}>
                    <Message.Header>Error</Message.Header>
                    <p>{error}</p>
                </Message>
            )}

            {evalError && (
                <Message negative style={{ marginBottom: '1rem' }}>
                    <Message.Header>평가를 시작하지 못했습니다</Message.Header>
                    <p>{evalError}</p>
                </Message>
            )}

            {SHOW_AI_COACHING && (
            <Button 
                toggle 
                active={monitoringEnabled} 
                onClick={toggleMonitoring} 
                color={monitoringEnabled ? 'yellow' : 'grey'}
                size='small'
                compact
                style={{ marginTop: '0.5rem', opacity: 0.9 , borderRadius: '20px'}}
            >
                <Icon name='lightbulb' />
                {monitoringEnabled ? 'AI 코칭 켜짐' : 'AI 코칭 꺼짐'}
            </Button>
            )}
        </div>

        {/* Middle Section: Transcripts */}
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: history.length ? 'center' : 'flex-start', padding: '2rem', pointerEvents: 'none', paddingBottom: '180px' }}>
             <div style={{ width: '100%', maxWidth: '800px', margin: '0 auto', pointerEvents: 'auto', display: 'flex', flexDirection: 'column', maxHeight: '100%', minHeight: 0 }}>
                {hintMounted && (
                    <div style={{
                        backgroundColor: 'rgba(31, 41, 55, 0.85)', color: '#fff', borderRadius: '14px',
                        padding: '14px 18px', marginBottom: '1rem', border: '1px solid rgba(255,255,255,0.12)',
                        fontSize: '14px', lineHeight: 1.6,
                        opacity: awaitingFirstWords ? 1 : 0, transition: 'opacity 0.8s ease',
                    }}>
                        {/* The scene the persona cards showed: who to approach and what they're doing —
                            what the trainee needs to open the conversation. Scene images are portrait,
                            so on desktop the photo sits beside the text to keep the card short; on a
                            phone they stack. Whole image at its own aspect ratio, never cropped. */}
                        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '10px' : '18px', alignItems: 'center' }}>
                            {(currentPersona?.situation_url || currentPersona?.avatar_url) && (
                                <img
                                    src={currentPersona.situation_url || currentPersona.avatar_url}
                                    alt=""
                                    style={{ display: 'block', flexShrink: 0, width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: isMobile ? '20vh' : '30vh', borderRadius: '10px' }}
                                />
                            )}
                            <div>
                                {currentPersona?.persona?.current_activity?.value && (
                                    <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '13px', marginBottom: '8px' }}>
                                        {currentPersona.persona.current_activity.value}
                                    </div>
                                )}
                                <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: '4px' }}>
                                    <Icon name='microphone' /> 먼저 말을 걸어 주세요
                                </div>
                                고객은 직원이 먼저 인사하기를 기다리고 있어요.
                                <div style={{ color: 'rgba(255,255,255,0.7)' }}>예) "안녕하세요, 무엇을 도와드릴까요?"</div>
                            </div>
                        </div>
                        {hintStuck && (
                            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.15)', color: '#FCD34D' }}>
                                아직 목소리가 들리지 않아요. 마이크가 켜져 있는지 확인해 주세요.
                                카카오톡 등 앱 안에서 열었다면 Safari·Chrome으로 열어 주세요.
                            </div>
                        )}
                    </div>
                )}
                {/* Chat history: the session's saved lines, this connection's, then the line in
                    progress. Half the screen height, centred, faded at both edges; the bottom
                    padding matches the bottom fade so the newest line stays fully visible.
                    No backdrop-filter on the bubbles: Safari draws those outside the mask. */}
                {(history.length > 0 || userTranscript || aiTranscript) && (
                    <div ref={historyBoxRef} style={{
                        // Shrinks below 50vh on short screens rather than making the page scroll.
                        height: '50vh', maxHeight: '100%', minHeight: 0, overflowY: 'auto', padding: '0 4px 9vh',
                        display: 'flex', flexDirection: 'column',
                        maskImage: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.25) 15%, black 45%, black 82%, transparent 100%)',
                        WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.25) 15%, black 45%, black 82%, transparent 100%)',
                        scrollbarWidth: 'none',
                    }}>
                        <div style={{ marginTop: 'auto' }}>{/* few lines sit at the bottom, like a chat */}
                        {[
                            ...history,
                            ...(userTranscript ? [{ speaker: 'H', text: userTranscript, live: true }] : []),
                            ...(aiTranscript ? [{ speaker: 'B', text: aiTranscript, live: true }] : []),
                        ].map((line, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: line.speaker === 'H' ? 'flex-end' : 'flex-start', marginBottom: '8px' }}>
                                <div style={{
                                    maxWidth: '80%', padding: '8px 14px', borderRadius: '16px',
                                    backgroundColor: line.speaker === 'H' ? 'rgba(59, 130, 246, 0.55)' : 'rgba(16, 185, 129, 0.35)',
                                    color: 'white', fontSize: isMobile ? '0.95rem' : '1.05rem', lineHeight: 1.5,
                                    opacity: line.live ? 0.75 : 1, whiteSpace: 'pre-wrap',
                                }}>
                                    {line.text}
                                </div>
                            </div>
                        ))}
                        </div>
                    </div>
                )}
                {SHOW_AI_COACHING && (
                <AICoachingPanel
                  guideMessage={guideMessage}
                  currentPhase={currentPhase}
                  customerInsights={customerInsights}
                  displayProduct={displayProduct}
                  pinnedProduct={pinnedProduct}
                  newSuggestion={newSuggestion}
                  handlePinProduct={handlePinProduct}
                  handleUpdatePinToNew={handleUpdatePinToNew}
                />
                )}
             </div>
        </div>
      </div>
      {/* Bottom Section: Controls */}
      <div style={{ position: 'absolute',bottom: isMobile ? '20px' : '60px', left: 0, width: '100%', display: 'flex', justifyContent: isMobile ? 'flex-start' : 'center', pointerEvents: 'auto', paddingLeft: isMobile ? '20px' : 0 }}>
            {!isConnected ? (
                isMobile ? (
                    <Button
                        color='green'
                        size='large'
                        onClick={connect}
                        loading={connecting}
                        disabled={connecting}
                        icon
                        circular
                        style={{
                            boxShadow: '0 4px 14px 0 rgba(0,0,0,0.39)',
                            width: '45px',
                            height: '45px',
                            minWidth: '45px',
                            minHeight: '45px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0
                        }}
                    >
                      <Icon
                        name='play'
                        style={{
                          margin: 0,
                          fontSize: '18px',
                          transform: 'translateX(1px)'
                        }}
                      />
                    </Button>
                ) : (
                    <Button color='green' size='huge' onClick={connect} disabled={connecting} icon labelPosition='left' circular style={{ boxShadow: '0 4px 14px 0 rgba(0,0,0,0.39)' }}>
                        <Icon name={connecting ? 'spinner' : 'play'} loading={connecting} />
                        {connecting ? '연결 중…' : '세션 시작'}
                    </Button>
                )
            ) : (
                isMobile ? (
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <Button
                            color='red'
                            size='large'
                            onClick={disconnect}
                            title={isPractice ? '세션 종료' : '평가 없이 종료'}
                            icon
                            circular
                            style={{
                                boxShadow: '0 4px 14px 0 rgba(0,0,0,0.39)',
                                width: '45px',
                                height: '45px',
                                minWidth: '45px',
                                minHeight: '45px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0
                            }}
                        >
                            <Icon
                              name='stop'
                              style={{
                                margin: 0,
                                fontSize: '16px',
                                transform: 'translateX(-2px) translateY(-1px)'
                              }}
                            />
                        </Button>
                        {!isPractice && (
                        <Button
                            primary
                            size='large'
                            onClick={() => setEvalConfirmOpen(true)}
                            disabled={!canEvaluate || isCreatingEval}
                            loading={isCreatingEval}
                            title={canEvaluate ? '종료하고 평가 받기' : '평가를 위해 최소 3턴 이상 필요'}
                            icon
                            circular
                            style={{
                                boxShadow: '0 4px 14px 0 rgba(0,0,0,0.39)',
                                width: '45px',
                                height: '45px',
                                minWidth: '45px',
                                minHeight: '45px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0
                            }}
                        >
                            <Icon name='clipboard check' style={{ margin: 0, fontSize: '16px' }} />
                        </Button>
                        )}
                    </div>
                ) : (
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <Button color='red' size='huge' onClick={disconnect} icon labelPosition='left' circular style={{ boxShadow: '0 4px 14px 0 rgba(0,0,0,0.39)' }}>
                            <Icon name='stop' />
                            종료
                        </Button>
                        {!isPractice && (
                        <Button
                            primary
                            size='huge'
                            onClick={() => setEvalConfirmOpen(true)}
                            disabled={!canEvaluate || isCreatingEval}
                            loading={isCreatingEval}
                            title={canEvaluate ? '' : '평가를 위해 최소 3턴 이상 필요'}
                            icon
                            labelPosition='left'
                            circular
                            style={{ boxShadow: '0 4px 14px 0 rgba(0,0,0,0.39)' }}
                        >
                            <Icon name='clipboard check' />
                            종료하고 평가 받기
                        </Button>
                        )}
                    </div>
                )
            )}
      </div>
      <CameraFeed videoRef={videoRef} isRecording={isRecording} />
      {audioStalled && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 2000, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div style={{ backgroundColor: '#1F2937', color: 'white', borderRadius: '16px', padding: '24px', maxWidth: '360px', textAlign: 'center', lineHeight: 1.6 }}>
            <Icon name='microphone slash' size='big' style={{ color: '#FCD34D', marginBottom: '8px' }} />
            <div style={{ fontWeight: 700, fontSize: '17px', marginBottom: '6px' }}>음성이 중단되었습니다</div>
            <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.8)', marginBottom: '16px' }}>
              알림·전화·다른 앱 전환 등으로 마이크가 멈췄어요. 아래 버튼을 눌러 이어서 진행해 주세요.
            </div>
            <Button primary size='large' circular onClick={restoreAudio}>
              <Icon name='redo' /> 다시 시작
            </Button>
          </div>
        </div>
      )}
      <Modal size='tiny' open={evalConfirmOpen} onClose={() => setEvalConfirmOpen(false)}>
        <Modal.Header>평가 실행</Modal.Header>
        <Modal.Content>
          <p style={{ fontSize: '15px', fontWeight: 600 }}>이 대화로 평가를 실행하시겠습니까?</p>
          <p style={{ color: '#86868b', fontSize: '13px' }}>세션이 종료되며, 평가는 세션당 한 번만 받을 수 있습니다.</p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setEvalConfirmOpen(false)}>취소</Button>
          <Button primary onClick={endSessionAndEvaluate} loading={isCreatingEval}>평가 실행</Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default AudioOrbOpenAI;
